// Calendar dates as "YYYY-MM-DD" text. Arithmetic uses UTC midnight internally only as a
// counting device; no value here ever depends on the device time zone.

export type IsoDate = string;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** Days since 1970-01-01 of a calendar date; null when the text is not one (2026-02-30). Parsed once. */
function epochDayOf(value: string): number | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  const ms = Date.UTC(y, m, d);
  const date = new Date(ms);
  return date.getUTCFullYear() === y && date.getUTCMonth() === m && date.getUTCDate() === d ? ms / DAY_MS : null;
}

export function isIsoDate(value: string): boolean {
  return epochDayOf(value) !== null;
}

function toEpochDay(date: IsoDate): number {
  const day = epochDayOf(date);
  if (day === null) throw new RangeError(`Not a calendar date: "${date}"`);
  return day;
}

function fromEpochDay(day: number): IsoDate {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return fromEpochDay(toEpochDay(date) + days);
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  return toEpochDay(to) - toEpochDay(from);
}

/** ISO weekday: Monday = 1 … Sunday = 7. */
export function weekday(date: IsoDate): number {
  const day = new Date(toEpochDay(date) * DAY_MS).getUTCDay();
  return day === 0 ? 7 : day;
}

/** Weeks start on Monday in both Poland and the UK. */
export function startOfWeek(date: IsoDate): IsoDate {
  return addDays(date, 1 - weekday(date));
}

export function startOfMonth(date: IsoDate): IsoDate {
  return date.slice(0, 8) + '01';
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function endOfMonth(date: IsoDate): IsoDate {
  const [y, m] = date.split('-').map(Number) as [number, number];
  return `${date.slice(0, 8)}${String(daysInMonth(y, m)).padStart(2, '0')}`;
}

/** Adds months, clamping the day (31 Jan + 1 month = 28/29 Feb). */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  const day = Math.min(d, daysInMonth(year, month));
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function startOfYear(date: IsoDate): IsoDate {
  return date.slice(0, 4) + '-01-01';
}

export function endOfYear(date: IsoDate): IsoDate {
  return date.slice(0, 4) + '-12-31';
}

// One formatter per time zone: making one is costly, and the app asks for today once per
// transaction in some lists (a sixth of the net worth page's time on a slowed-down processor).
const dayFormats = new Map<string, Intl.DateTimeFormat>();

/** Today's calendar date in a given IANA time zone (default: the device's). */
export function todayIn(timeZone?: string, now: Date = new Date()): IsoDate {
  let format = dayFormats.get(timeZone ?? '');
  if (!format) {
    format = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
    dayFormats.set(timeZone ?? '', format);
  }
  const parts = format.formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
